import { useEffect, useState } from 'react';
import { compose, dissoc, dissocPath, evolve, map, modify, omit, pathEq, prop, when } from 'ramda';
import { getZipCodeRules } from '@@viewHelpers/zipCode';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import { dateTimeToMidday, dateTsToSystem } from '@@viewHelpers/date.js';
import useSaveSouscriptionCustomerGranteesMutation from './useSaveSouscriptionCustomerGranteesMutation.js';
import { formatExtraDataBeforeSubmit } from '../extra/formatExtraData.js';

const useSouscriptionCustomerGranteesForm = ({ project: data, middleFeatures, form, grantees }) => {
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({
    display: false,
    message: ''
  });
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const { processHandlers, context } = useProcess();

  const saveDetails = useSaveSouscriptionCustomerGranteesMutation();

  const minBirthDateReducChild = dateTsToSystem(prop('minBirthDateReducChild', data));
  const maxBirthDateReducChild = dateTsToSystem(prop('maxBirthDateReducChild', data));
  const minBirthDateHospiChild = dateTsToSystem(prop('minBirthDateHospiChild', data));
  const maxBirthDateHospiChild = dateTsToSystem(prop('maxBirthDateHospiChild', data));
  const offerCode = prop('offerCode', data);

  useEffect(() => {
    setIsUnauthorized(false);
  }, [
    form.watch('customer.firstName'),
    form.watch('customer.lastName'),
    form.watch('customer.birthDate')
  ]);

  const { isValid } = form.formState;

  const formatBirthDateToString = modify('birthDate', (birthDate) =>
    dateTimeToMidday(birthDate).valueOf().toString()
  );

  const onSubmit = (values) => {
    if (loading) return;
    setLoading(true);

    values = compose(
      evolve({
        grantees: map(
          compose(formatBirthDateToString, dissocPath(['attachedSocialSecurities', 0, 'referent']))
        ),
        customer: formatBirthDateToString
      }),
      dissoc('activePolicy'),
      when(pathEq(['customer', 'isMadelin'], false), dissocPath(['customer', 'madelinMonth'])),
      omit([
        'offerCode',
        'minBirthDateReducChild',
        'maxBirthDateReducChild',
        'minBirthDateHospiChild',
        'maxBirthDateHospiChild',
        'defaultGroupCode',
        'groupCodes'
      ]),
      formatExtraDataBeforeSubmit
    )(values);

    saveDetails.onFormSubmit(values, (ok, error) => {
      if (ok && !error) {
        processHandlers.onNext({
          context: {
            projectId: context?.projectId
          }
        });
      } else {
        setLoading(false);
      }
    });
  };

  const { ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES = [] } = middleFeatures;
  const postalCodeFranceMetro = getZipCodeRules(
    'France Métropolitaine',
    ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES
  );
  const postalCodeDROM = getZipCodeRules('DROM', ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES);

  const onPrevious = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  return {
    onSubmit,
    modal,
    setModal,
    isUnauthorized,
    postalCodeFranceMetro,
    postalCodeDROM,
    onPrevious,
    minBirthDateReducChild,
    maxBirthDateReducChild,
    minBirthDateHospiChild,
    maxBirthDateHospiChild,
    offerCode,
    grantees,
    isValid
  };
};

export default useSouscriptionCustomerGranteesForm;
